import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRight, 
  Check, 
  Zap, 
  Code, 
  Cpu, 
  Layout, 
  Gamepad2, 
  Users, 
  ExternalLink,
  Factory,
  Terminal,
 Mail,
  Play
} from 'lucide-react';
import { Button } from '../components/Button'; // Assuming this exists from your prompt

// --- CONFIGURATION: SHOWCASE GAMES ---
// Modify this array to update the games shown on the landing page
const SHOWCASE_GAMES = [
  {
    id: 1,
    name: 'Neon City Tycoon',
    players: '1.2M+ Visits',
    thumbnail: 'https://tr.rbxcdn.com/180 DAY-c6d9620023a9d91f24e9347585c5457a/768/432/Image/Png', // Replace with real Roblox asset URLs
    url: 'https://www.roblox.com',
    description: 'A cyberpunk economic simulation built entirely with BloxBuddy logic.'
  },
  {
    id: 2,
    name: 'Obby Legends',
    players: '850K+ Visits',
    thumbnail: 'https://tr.rbxcdn.com/180 DAY-593885c3b953930f785d0335cc24fa21/768/432/Image/Png',
    url: 'https://www.roblox.com',
    description: 'Generated procedural obstacle courses and save systems.'
  },
  {
    id: 3,
    name: 'Blade Master RPG',
    players: '45k Concurrent',
    thumbnail: 'https://tr.rbxcdn.com/180 DAY-24879207869686071855734316666174/768/432/Image/Png',
    url: 'https://www.roblox.com',
    description: 'Complex inventory and combat systems coded in minutes.'
  },
  {
    id: 4,
    name: 'Pet Simulator AI',
    players: '2.5M+ Visits',
    thumbnail: 'https://tr.rbxcdn.com/180 DAY-9c20a9a081525cb92d27a15104d4122d/768/432/Image/Png',
    url: 'https://www.roblox.com',
    description: 'Pet hatching logic and trade systems generated automatically.'
  }
];

export default function Landing() {
  const navigate = useNavigate();

function Section({ id, title, children }) {                return (                                                   <section id={id} className="scroll-mt-32 animate-in fade-in slide-in-from-bottom-4 duration-700">                 <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">                                       {title}                                            </h2>                                                  <div className="space-y-4 text-slate-400 leading-relaxed text-base">                                              {children}                                         </div>                                             </section>                                         );                                                 };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-sans selection:bg-blue-500 selection:text-white overflow-x-hidden">

      {/* --- NAVBAR --- */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/logo512.png" alt="Logo" className="w-8 h-8 rounded-lg" />
            <span className="font-bold text-xl text-white tracking-tight">BloxBuddy</span>
          </div>
          
          <div className="hidden md:flex items-center gap-8">
            <a href="#showcase" className="text-sm font-medium text-slate-400 hover:text-white transition-colors">Made with BloxBuddy</a>
            <a href="#features" className="text-sm font-medium text-slate-400 hover:text-white transition-colors">Features</a>
            <a href="#pricing" className="text-sm font-medium text-slate-400 hover:text-white transition-colors">Pricing</a>
          </div>

          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/auth')} className="text-sm font-medium text-white hover:text-blue-400 transition-colors hidden sm:block">
              Log in
            </button>
            <Button onClick={() => navigate('/console')} className="bg-blue-600 hover:bg-blue-500 text-white rounded-full px-6 py-2">
              Launch Console
            </Button>
          </div>
        </div>
      </nav>

      {/* --- HERO SECTION --- */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[120px] -z-10 pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-indigo-600/10 rounded-full blur-[100px] -z-10 pointer-events-none" />

        <div className="max-w-4xl mx-auto text-center space-y-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-900/30 border border-blue-500/30 text-blue-300 text-xs font-bold uppercase tracking-wider animate-in fade-in slide-in-from-bottom-4">
            <Zap size={12} className="fill-blue-400" />
          AI-Powered Roblox Building
          </div>

          <h1 className="text-5xl md:text-7xl font-extrabold text-white tracking-tight leading-tight animate-in fade-in slide-in-from-bottom-6 duration-700">
            Vibe-build Roblox Games at <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-300">
               10x Speed
            </span>
          </h1>

          <p className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed animate-in fade-in slide-in-from-bottom-8 duration-1000">
            Describe your game and BloxBuddy generates optimized, error-free Lua scripts, GDD, levels, UI layouts, and server-side logic instantly. 
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4 animate-in fade-in slide-in-from-bottom-10 duration-1000">
            <button
              onClick={() => navigate('/console')}
              className="px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold text-lg shadow-[0_0_40px_rgba(37,99,235,0.3)] transition-all hover:scale-105 flex items-center gap-2"
            >
              Start Building Free <ArrowRight size={20} />
            </button>
            <button className="px-8 py-4 bg-slate-900 hover:bg-slate-800 border border-slate-700 text-white rounded-xl font-bold text-lg transition-all flex items-center gap-2">
              <Play size={18} className="fill-white" /> Watch Demo
            </button>
          </div>

          {/* Console Preview */}
        {/*  <div className="mt-16 relative mx-auto max-w-5xl rounded-xl border border-slate-800 bg-slate-900/80 p-1 shadow-2xl animate-in fade-in zoom-in duration-1000 delay-200 backdrop-blur-sm">
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent z-10 rounded-xl pointer-events-none" />
            

	   <img
              src="./homehome.jpg"
              alt="BloxBuddy Console"
              className="rounded-lg w-full h-auto opacity-90"
            />
	    

          </div> */}
        </div>
	

      </section>

      {/* --- GAME SHOWCASE (Horizontal Scroll) --- */}
      <section id="showcase" className="py-24 bg-slate-950 border-t border-slate-900">
        <div className="max-w-7xl mx-auto px-6 mb-12 flex items-end justify-between">
            <div>
                <h2 className="text-3xl font-bold text-white mb-2">Built with BloxBuddy</h2>
                <p className="text-slate-400">Join the creators shipping top-tier games.</p>
            </div>
            <div className="hidden md:flex gap-2 text-slate-500 text-sm">
                <span>Scroll to explore</span> <ArrowRight size={16} />
            </div>
        </div>

        {/* Horizontal Scroll Container */}
        <div className="w-full overflow-x-auto pb-8 hide-scrollbar px-6 flex gap-6 snap-x snap-mandatory">
            {SHOWCASE_GAMES.map((game) => (
                <div 
                    key={game.id} 
                    className="min-w-[300px] md:min-w-[400px] bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden snap-center group hover:border-blue-500/50 transition-all duration-300"
                >
                    <div className="h-48 overflow-hidden relative">
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent z-10" />
                        <img 
                            src={game.thumbnail} 
                            alt={game.name} 
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-3 right-3 z-20 bg-black/60 backdrop-blur-md px-2 py-1 rounded text-xs font-bold text-white flex items-center gap-1">
                            <Users size={12} className="text-green-400" /> {game.players}
                        </div>
                    </div>
                    
                    <div className="p-5">
                        <h3 className="text-xl font-bold text-white mb-1">{game.name}</h3>
                        <p className="text-slate-400 text-sm mb-4 line-clamp-2">{game.description}</p>
                        
                        <a 
                            href={game.url} 
                            target="_blank" 
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 text-sm font-medium text-blue-400 hover:text-blue-300 transition-colors"
                        >
                            Play on Roblox <ExternalLink size={14} />
                        </a>
                    </div>
                </div>
            ))}
        </div>
      </section>

      {/* --- FEATURES GRID --- */}
      <section id="features" className="py-24 bg-slate-900/30 relative border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Complete Development Suite</h2>
            <p className="text-slate-400">Everything you need to script, refine, and deploy.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <FeatureCard
              icon={<Factory className="text-blue-400" />}
              title="Automate Everything"
              desc="You guide, we execute, just prompt what you'd like, connect the plugin and watch your ideas turn to full games."
            />


	  <FeatureCard                                             icon={<Terminal className="text-blue-400" />}                                                                 title="Instant Lua Scripting"                            desc="Forget syntax errors. Generate complex systems like Datastores, Leaderboards, and Round Systems in seconds."                                               />

	  {/*   <FeatureCard
              icon={<Layout className="text-purple-400" />}
              title="UI Layout Generation"
              desc="Describe your HUD or Menu, and get the exact ScreenGui structure and LocalScripts to make it functional."
            />
	    */}

            <FeatureCard
              icon={<Cpu className="text-green-400" />}
              title="Automatic Logic Debugging"
              desc="We'll your broken code, and BloxBuddy will identify logic errors, memory leaks, and optimize performance from the editor directly, no work on your side."
            />
          </div>
        </div>
      </section>

      {/* --- PRICING SECTION --- */}
      <section id="pricing" className="py-24 bg-slate-950 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Simple, transparent pricing</h2>
            <p className="text-slate-500">
{/*	  Start building for free. Upgrade for power.
	   </p> 
<br /> */}

	{/* <p className="text-slate-400"> */}

	  *light credits are used by workers and diamond credits are uses by PM models</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Free Tier */}
            <PricingCard
              tier="Starter"
              price="$0"
              desc="Perfect for learning Luau scripting."
              features={[
		      '100k light credits',                               '20k diamond credits',                                  'Community Support']}
              cta="Start Free"
              onClick={() => navigate('/console')}
            />

            {/* Pro Tier (Highlighted) */}
            <PricingCard
              tier="Basic Creator"
              price="$20"
              period="/mo"
              desc="For developers shipping games."
              features={[
		      '3milion light credits', 
		      '200k diamond credits', 
		      'Unlimited projects',
	 'Unused credits carryover to next month', 
		      'Community Support'
		      
	      ]}
              cta="Go Pro"
              // highlighted={true}
              onClick={() => navigate('/pricing')}
            />

            {/* Studio Tier */}
            <PricingCard
              tier="Lightening Creator"
              price="$40"
              period="/mo"
              desc="For teams, big games and heavy workloads."
              features={[
		      '8milion light credits',                               '400k diamond credits',                                'Unlimited projects',                                  'Community Support',
	'Unused credits carryover to next month',
		 'Team Collaboration (coming soon)',
		     'Dedicated Support'
	      ]}
               cta="Go Bolting"

          highlighted={true}
              onClick={() => navigate('/pricing')}
            />
	    
          </div>
        </div>
      </section>

	  <Section id="contact" title="">                                                                                                         <div className="mt-6 bg-slate-900 border border-slate-800 rounded-lg p-6">                                        <div className="flex items-center gap-3 text-white font-medium mb-2">                                             <Mail className="text-blue-400" size={20} /> Email Support                                                </div>                  <a href="mailto:publicstring.care@gmail.com" className="text-slate-400 hover:text-white transition-colors">                                              publicstring.care@gmail.com                                    </a>                                               </div>                                             </Section>
	

      {/* --- FOOTER --- */}
      <footer className="pt-20 pb-10 border-t border-slate-800 bg-slate-950 text-slate-400 text-sm">

        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-10 mb-16">
            <div className="col-span-2 md:col-span-1">
                <div className="flex items-center gap-2 mb-4">
                    <img src="/logo512.png" alt="Logo" className="w-6 h-6 rounded-md" />
                    <span className="font-bold text-white text-lg">BloxBuddy</span>
                </div>
                <p className="text-slate-500 mb-6">
                    Empowering the next generation of Roblox developers with AI tools.
                </p>
            </div>

            
            <div>
                <h4 className="font-bold text-white mb-4">Product</h4>
                <ul className="space-y-3">
                    <li><a href="#features" className="hover:text-blue-400 transition-colors">Features</a></li>
                    <li><a href="#pricing" className="hover:text-blue-400 transition-colors">Pricing</a></li>
                    <li><a href="#showcase" className="hover:text-blue-400 transition-colors">Showcase</a></li>
                </ul>
            </div>

            <div>
                <h4 className="font-bold text-white mb-4">Resources</h4>
                <ul className="space-y-3">
                    <li><a href="#" className="hover:text-blue-400 transition-colors">Get Editor plugin</a></li>
                    <li><a href="https://discord.gg/qCYRjK4pd3" className="hover:text-blue-400 transition-colors">Discord Community</a></li>
	  {/* <li><a href="#" className="hover:text-blue-400 transition-colors">Blog</a></li> */}
                </ul>
            </div>

            <div>
                <h4 className="font-bold text-white mb-4">Legal</h4>
                <ul className="space-y-3">
                    <li><button onClick={() => navigate('/terms')} className="hover:text-blue-400 transition-colors text-left">Terms of Service</button></li>
                    <li><button onClick={() => navigate('/privacy')} className="hover:text-blue-400 transition-colors text-left">Privacy Policy</button></li>
                </ul>
            </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 pt-8 border-t border-slate-900 flex flex-col md:flex-row items-center justify-between gap-4">
            <p>&copy; {new Date().getFullYear()} BloxBuddy. All rights reserved.</p>
            <p className="text-xs text-slate-600">
                Not affiliated with Roblox Corporation. Roblox is a registered trademark of Roblox Corp.
            </p>
        </div>
      </footer>

    </div>
  );
}

// --- SUB COMPONENTS ---
function FeatureCard({ icon, title, desc }) {
return (
<div className="p-8 rounded-2xl bg-slate-900 border border-slate-800 hover:border-blue-500/30 transition-all hover:bg-slate-900/80">
<div className="w-12 h-12 rounded-lg bg-slate-800 flex items-center justify-center mb-6">
{icon}
</div>
<h3 className="text-xl font-bold text-white mb-3">{title}</h3>
<p className="text-slate-400 leading-relaxed">{desc}</p>
</div>
);
};

function PricingCard({ tier, price, period = "", desc, features, cta, highlighted = false, onClick }) {
  return (
    <div className={`p-8 rounded-2xl border flex flex-col relative transition-all duration-300 ${
      highlighted
      ? 'bg-slate-900 border-blue-500 shadow-[0_0_40px_rgba(37,99,235,0.1)] scale-100 md:scale-105 z-10'
      : 'bg-slate-950 border-slate-800 hover:border-slate-700'
    }`}>
      {highlighted && (
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-blue-600 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full border border-blue-400">
          Most Popular
        </div>
      )}
      <div className="mb-6">
        <h3 className="text-lg font-bold text-slate-200 mb-2">{tier}</h3>
        <div className="flex items-baseline gap-1">
          <span className="text-4xl font-extrabold text-white tracking-tight">{price}</span>
          <span className="text-slate-500 font-medium">{period}</span>
        </div>
        <p className="text-slate-400 text-sm mt-4">{desc}</p>
      </div>

      <div className="space-y-4 mb-8 flex-1">
        {features.map((feat, i) => (
          <div key={i} className="flex items-start gap-3 text-sm text-slate-300/90">
            <Check size={16} className="text-blue-500 shrink-0 mt-0.5" />
            <span className="leading-tight">{feat}</span>
          </div>
        ))}
      </div>

      <button
        onClick={onClick}
        className={`w-full py-3.5 rounded-xl font-bold text-sm transition-all ${
          highlighted
          ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-900/20'
          : 'bg-slate-800 hover:bg-slate-700 text-white border border-slate-700'
        }`}
      >
        {cta}
      </button>
    </div>
  );
}
